/**
 * Control primitives. Every interactive element is at least 44px on its short
 * axis, carries a label or aria-label, and shows a visible focus ring — this is
 * a phone instrument, and a control you cannot hit is a control you do not have.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') n.className = v;
    else if (k === 'html') n.innerHTML = v;
    else n.setAttribute(k, v);
  }
  for (const c of children) n.append(c);
  return n;
}

export interface SliderOpts {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  format?: (v: number) => string;
  onInput: (v: number) => void;
}

export function slider(o: SliderOpts): HTMLElement {
  const id = `s-${o.label.toLowerCase().replace(/\W+/g, '-')}-${Math.random().toString(36).slice(2, 7)}`;
  const read = el('b', { class: 'read', id: `${id}-read` });
  const fmt = o.format ?? ((v: number) => String(v));
  read.textContent = fmt(o.value);
  const input = el('input', {
    type: 'range', id,
    min: String(o.min), max: String(o.max), step: String(o.step), value: String(o.value),
    'aria-describedby': `${id}-read`,
  });
  input.addEventListener('input', () => {
    const v = Number(input.value);
    read.textContent = fmt(v);
    o.onInput(v);
  });
  return el('label', { class: 'slider', for: id }, [
    el('span', { class: 'slabel' }, [o.label]), input, read,
  ]);
}

export interface ChipsOpts<T extends string> {
  label: string;
  options: { id: T; label: string; title?: string; disabled?: boolean }[];
  value: T;
  onChange: (id: T) => void;
}

export function chips<T extends string>(o: ChipsOpts<T>): HTMLElement {
  const group = el('div', { class: 'chips', role: 'radiogroup', 'aria-label': o.label });
  let current = o.value;
  const buttons = new Map<T, HTMLButtonElement>();
  for (const opt of o.options) {
    const b = el('button', {
      class: 'chip', type: 'button', role: 'radio',
      'aria-checked': String(opt.id === current),
      ...(opt.title ? { title: opt.title } : {}),
      ...(opt.disabled ? { disabled: 'true' } : {}),
    }, [opt.label]) as HTMLButtonElement;
    b.addEventListener('click', () => {
      current = opt.id;
      for (const [id, btn] of buttons) btn.setAttribute('aria-checked', String(id === current));
      o.onChange(opt.id);
    });
    buttons.set(opt.id, b);
    group.append(b);
  }
  return el('div', { class: 'field' }, [el('span', { class: 'slabel' }, [o.label]), group]);
}

export function toggle(label: string, pressed: boolean, onChange: (v: boolean) => void, title?: string): HTMLButtonElement {
  const b = el('button', {
    class: 'chip toggle', type: 'button', 'aria-pressed': String(pressed),
    ...(title ? { title } : {}),
  }, [label]) as HTMLButtonElement;
  b.addEventListener('click', () => {
    const next = b.getAttribute('aria-pressed') !== 'true';
    b.setAttribute('aria-pressed', String(next));
    onChange(next);
  });
  return b;
}

export function button(label: string, onClick: () => void, cls = ''): HTMLButtonElement {
  const b = el('button', { class: `btn ${cls}`.trim(), type: 'button' }, [label]) as HTMLButtonElement;
  b.addEventListener('click', onClick);
  return b;
}

export function panel(title: string, note: string, body: HTMLElement[]): HTMLElement {
  return el('section', { class: 'panel' }, [
    el('header', { class: 'panel-head' }, [
      el('h2', {}, [title]),
      el('p', { class: 'note' }, [note]),
    ]),
    el('div', { class: 'panel-body' }, body),
  ]);
}
